import React from "react";
import {
  Badge,
  Box,
  Link,
  Popover,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  ErrorCategorySource,
  InvestigationCategoryScope,
  InvestigationPredicate,
} from "../../lusca-search-api/generated-src";
import {
  excludedValues,
  isExclusion,
  predicateKey,
  predicateOperatorText,
  predicateValues,
  ruleFieldLabel,
} from "./investigationUtils";

type CategorySources = Record<string, ErrorCategorySource>;

const formatSourceDate = (iso?: string): string | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? undefined
    : d.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

/** What the category popover says: the CR that added the category's rule to the Glue job. */
const CategorySourceContent: React.FC<{ source: ErrorCategorySource }> = ({
  source,
}) => {
  const href = source.crUrl || source.commitUrl;
  const label = source.cr || source.commit.slice(0, 8);
  const byline = [source.author, formatSourceDate(source.date)]
    .filter(Boolean)
    .join(", ");
  return (
    <SpaceBetween size="xxs">
      <Box>
        {source.migrated ? "Moved into code in " : "Added in "}
        <Link href={href} external>
          {label}
        </Link>
      </Box>
      {source.migrated ? (
        <Box fontSize="body-s" color="text-body-secondary">
          Earlier history is in QuickSight, not git.
        </Box>
      ) : (
        <>
          {source.title && <Box fontSize="body-s">{source.title}</Box>}
          {byline && (
            <Box fontSize="body-s" color="text-body-secondary">
              {byline}
            </Box>
          )}
        </>
      )}
    </SpaceBetween>
  );
};

/** The value side of a predicate: one code box per value, "or"-separated for an `in`. */
const Values: React.FC<{
  predicate: InvestigationPredicate;
  categorySources?: CategorySources;
}> = ({ predicate, categorySources }) => {
  const sources =
    (predicate.field as string) === "error_category" && !isExclusion(predicate)
      ? categorySources
      : undefined;
  const values = isExclusion(predicate)
    ? excludedValues(predicate)
    : predicateValues(predicate);
  return (
    <span
      style={{
        display: "inline-flex",
        flexWrap: "wrap",
        alignItems: "baseline",
        gap: "4px 6px",
      }}
    >
      {values.map((v, i) => (
        <React.Fragment key={v}>
          {i > 0 && (
            <Box
              fontSize="body-s"
              color="text-body-secondary"
              display="inline-block"
            >
              or
            </Box>
          )}
          <Box variant="code" fontSize="body-s" display="inline-block">
            {sources?.[v] ? (
              <span
                style={{ overflowWrap: "anywhere" }}
                data-testid="rule-category-source"
              >
                <Popover
                  triggerType="text"
                  dismissButton={false}
                  size="medium"
                  position="bottom"
                  content={<CategorySourceContent source={sources[v]} />}
                >
                  {v}
                </Popover>
              </span>
            ) : (
              <span style={{ overflowWrap: "anywhere" }}>{v}</span>
            )}
          </Box>
        </React.Fragment>
      ))}
    </span>
  );
};

interface InvestigationRuleProps {
  rule?: Array<InvestigationPredicate> | null;
  /**
   * Several OR branches (a merged investigation). When given and longer than one, `rule` is
   * ignored and every branch is rendered, separated by an OR divider.
   */
  rules?: Array<Array<InvestigationPredicate>> | null;
  /** Compact = single wrapped line of chips (drawer); default = one predicate per row. */
  compact?: boolean;
  /**
   * Set when the case was created from `error_category = uncategorized`: the stored rule dropped
   * that pin (and may exclude categories the pattern already had). The rule is shown as picked,
   * with what was actually stored underneath.
   */
  categoryScope?: InvestigationCategoryScope | null;
  /**
   * Category → the CR that added it (GET /investigations/category-sources). When given, each
   * error_category value with a known source opens a popover linking that CR.
   */
  categorySources?: CategorySources;
}

/** The "as picked" error-category row of a case created from an uncategorised rule. */
const CategoryScopeNote: React.FC<{ scope: InvestigationCategoryScope }> = ({
  scope,
}) => {
  const excluded = scope.excludedCategories ?? [];
  return (
    <Box
      fontSize="body-s"
      color="text-body-secondary"
      data-testid="rule-category-scope"
    >
      Started from items that were uncategorised; keeps tracking them after they
      are categorised
      {excluded.length > 0 && (
        <>
          {" "}
          (except items already categorised as{" "}
          {excluded.map((c, i) => (
            <React.Fragment key={c}>
              {i > 0 && ", "}
              <Box variant="code" fontSize="body-s" display="inline-block">
                {c}
              </Box>
            </React.Fragment>
          ))}
          )
        </>
      )}
      .
    </Box>
  );
};

/** The predicate the scope stands in for: the stored error_category exclusion. */
const isScopedCategory = (p: InvestigationPredicate) =>
  (p.field as string) === "error_category" && isExclusion(p);

/** A synthetic `error_category = uncategorized` predicate, shown in place of the stored form. */
const pickedCategory = (
  scope: InvestigationCategoryScope
): InvestigationPredicate => ({
  field: "error_category" as InvestigationPredicate["field"],
  operator: "eq" as InvestigationPredicate["operator"],
  value: scope.selected || "uncategorized",
});

const chip = (p: InvestigationPredicate) => (
  <SpaceBetween
    key={predicateKey(p)}
    direction="horizontal"
    size="xxs"
    alignItems="center"
  >
    <Badge color="blue">{ruleFieldLabel(p.field)}</Badge>
    <Box color="text-body-secondary" fontSize="body-s">
      {predicateOperatorText(p)}
    </Box>
    <Values predicate={p} />
  </SpaceBetween>
);

/** Stable key for a branch: its predicates, which are unique within one rule. */
const branchKey = (branch: InvestigationPredicate[]): string =>
  branch.map(predicateKey).join("&");

const CompactBranch: React.FC<{ predicates: InvestigationPredicate[] }> = ({
  predicates,
}) => (
  <SpaceBetween direction="horizontal" size="xs" alignItems="center">
    {predicates.map((p, i) => (
      <React.Fragment key={predicateKey(p)}>
        {i > 0 && (
          <Box fontWeight="bold" fontSize="body-s" color="text-body-secondary">
            AND
          </Box>
        )}
        {chip(p)}
      </React.Fragment>
    ))}
  </SpaceBetween>
);

// One predicate per row. A plain non-wrapping flex row (not SpaceBetween, which wraps) so a
// long value shrinks and wraps *inside* its code box instead of dropping under the badge.
const FullBranch: React.FC<{
  predicates: InvestigationPredicate[];
  /** Keyword on the first row: WHERE for the first branch, OR for the following ones. */
  lead: string;
  categorySources?: CategorySources;
}> = ({ predicates, lead, categorySources }) => (
  <SpaceBetween size="xxs">
    {predicates.map((p, i) => (
      <div
        key={predicateKey(p)}
        style={{
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "baseline",
          gap: "6px",
        }}
      >
        <Box fontWeight="bold" fontSize="body-s" color="text-body-secondary">
          <span
            style={{
              display: "inline-block",
              minWidth: "3.2em",
              textAlign: "right",
            }}
          >
            {i === 0 ? lead : "AND"}
          </span>
        </Box>
        <span style={{ flex: "0 0 auto" }}>
          <Badge color="blue">{ruleFieldLabel(p.field)}</Badge>
        </span>
        <Box color="text-body-secondary" fontSize="body-s">
          {predicateOperatorText(p)}
        </Box>
        <span style={{ flex: "1 1 auto", minWidth: 0 }}>
          <Values predicate={p} categorySources={categorySources} />
        </span>
      </div>
    ))}
  </SpaceBetween>
);

/**
 * Visualises a population rule as "field is value" chips joined by AND (an `in` predicate reads
 * "field is one of a or b"), instead of a raw `a = b AND c IN (…)` string. Every DLQ item
 * matching all predicates belongs to the population.
 * A merged investigation has several branches: an item belongs when ANY branch matches, so the
 * branches are shown one after another separated by OR.
 */
export const InvestigationRule: React.FC<InvestigationRuleProps> = ({
  rule,
  rules,
  compact = false,
  categoryScope,
  categorySources,
}) => {
  const branches = (rules ?? []).filter((b) => b && b.length);
  const stored: InvestigationPredicate[][] = branches.length
    ? branches
    : rule && rule.length
    ? [rule]
    : [];
  if (!stored.length) {
    return <Box color="text-body-secondary">No rule (manual population)</Box>;
  }
  // Only a single, unmerged branch still reads as the creator picked it.
  const scope = categoryScope && stored.length === 1 ? categoryScope : null;
  const effective = scope
    ? [
        [
          ...stored[0].filter((p) => !isScopedCategory(p)),
          pickedCategory(scope),
        ],
      ]
    : stored;
  if (scope && !compact) {
    return (
      <SpaceBetween size="xxs">
        <FullBranch
          predicates={effective[0]}
          lead="WHERE"
          categorySources={categorySources}
        />
        <CategoryScopeNote scope={scope} />
      </SpaceBetween>
    );
  }
  if (compact) {
    return (
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        {effective.map((branch, i) => (
          <React.Fragment key={branchKey(branch)}>
            {i > 0 && (
              <Badge color="grey" data-testid="rule-or">
                OR
              </Badge>
            )}
            <CompactBranch predicates={branch} />
          </React.Fragment>
        ))}
      </SpaceBetween>
    );
  }
  return (
    <SpaceBetween size="xs">
      {effective.map((branch, i) => (
        <FullBranch
          key={branchKey(branch)}
          predicates={branch}
          lead={i === 0 ? "WHERE" : "OR"}
          categorySources={categorySources}
        />
      ))}
    </SpaceBetween>
  );
};

export default InvestigationRule;
